import uuidv4 from 'uuid/v4';
import {
    groupArrayOfObjectsByFieldValue,
    isArrayValidAndNotEmpty,
    isValidTextAndNotEmpty,
} from '../../constants/CommonUtil';
import { applicationDateFormat, PAYMENT_METHODS_MAP } from '../../constants/constants';
import { formatDate, parseDate } from '../../constants/DateUtil';
import { NumberOf } from '../../constants/numberUtils';

export const ADD_CASH_COLLECTION_FORM = 'addCashCollectionForm';

export const getInitialValues = (
    denominations = [],
    bankTransactions = [],
    defaultCashCloseType = null,
) => {
    const cashCollections = [];
    const otherCollections = [];
    if (isArrayValidAndNotEmpty(denominations)) {
        denominations.forEach((d) => {
            cashCollections.push({
                uiUuid: uuidv4(),
                denomination: d,
                quantity: '',
                amount: '',
            });
        });
    }
    if (isArrayValidAndNotEmpty(bankTransactions)) {
        bankTransactions.forEach((d) => {
            otherCollections.push({
                uiUuid: uuidv4(),
                label: d.label,
                paymentMethod: d.paymentMethod,
                amount: '',
                ...(d.bankName && { bankName: d.bankName }),
            });
        });
    }
    return ({
        cashCollections,
        otherCollections,
        subCompany: defaultCashCloseType,
        date: new Date(),
    });
};

const mapOtherPaymentOptionsToTransactions = (otherPaymentOptions = []) => {
    if (isArrayValidAndNotEmpty(otherPaymentOptions)) {
        return otherPaymentOptions.map(({ label, value }) => ({
            label,
            bankName: value,
            paymentMethod: PAYMENT_METHODS_MAP.OTHER.value,
        }));
    }
    return [];
};

export const populateOtherPaymentMethodsFromConfig = (
    current,
    configPaymentOptions,
    hideOtherPaymentModeInMultimodePayment,
) => {
    if (!isArrayValidAndNotEmpty(configPaymentOptions) || hideOtherPaymentModeInMultimodePayment) {
        return current;
    }
    const mappedOptions = mapOtherPaymentOptionsToTransactions(configPaymentOptions);
    return [...new Set([...current, ...mappedOptions])];
};

export const myCashCloseTableSchema = {
    fieldType: 'materialTable',
    id: 'table',
    name: 'myCashClose',
    showInNonEditableMode: true,
    showInReadOnlyMode: true,
    hideCheckBoxIcon: true,
    rowstyle: {
        height: '56px',
    },
    headerColStyle: {
        color: '#FFFFFF',
        fontSize: '14px',
    },
    tableCells: [
        {
            id: 'date',
            name: 'date',
            fieldType: 'text',
            find: 'date',
            label: 'Date',
            fullWidth: true,
        },
        {
            id: 'total',
            name: 'total',
            fieldType: 'text',
            find: 'amount',
            label: 'Total',
            fullWidth: true,
        },
        {
            id: 'dateCreated',
            name: 'dateCreated',
            fieldType: 'datetime',
            find: 'dateCreated',
            label: 'Created on',
        },
        {
            id: 'writeDate',
            name: 'writeDate',
            fieldType: 'datetime',
            find: 'writeDate',
            label: 'Last Updated on',
        },

        {
            id: 'remark',
            name: 'remark',
            fieldType: 'text',
            find: 'notes',
            label: 'Remarks',
            fullWidth: true,
        },
        {
            id: 'printIcon',
            name: 'printIcon',
            fieldType: 'printIcon',
            find: 'printIcon',
            label: '',
            fullWidth: true,
            cellStyle: {
                width: '60px',
            },
        },
        {
            id: 'deleteIcon',
            name: 'deleteIcon',
            fieldType: 'deleteIcon',
            find: 'deleteIcon',
            label: '',
            fullWidth: true,
            cellStyle: {
                width: '60px',
            },
        },
    ],
    grid: {
        sm: 12,
        md: 12,
        lg: 12,
    },
};

export const mapUiObjectToPayload = (uiObject = {}) => {
    const payload = {
        uuid: uiObject.uuid,
        subCompany: uiObject.subCompany.value,
        employee: uiObject.user || null,
        date: formatDate(uiObject.date, 'yyyy-MM-dd'),
        refundAmount: uiObject.refundAmount,
        notes: uiObject.remarks,
        collections: [],
        amountInSystem: uiObject.collectionInSystem,
    };
    const collections = [];
    if (isArrayValidAndNotEmpty(uiObject.cashCollections)) {
        uiObject.cashCollections.forEach((cash) => {
            if (NumberOf(cash.quantity) > 0) {
                collections.push({
                    paymentMethod: PAYMENT_METHODS_MAP.CASH.value,
                    denomination: cash.denomination,
                    quantity: cash.quantity,
                });
            }
        });
    }
    if (isArrayValidAndNotEmpty(uiObject.otherCollections)) {
        uiObject.otherCollections.forEach((cash) => {
            if (NumberOf(cash.amount > 0)) {
                collections.push({
                    paymentMethod: cash.paymentMethod,
                    amount: cash.amount,
                    name: cash.label,
                    ...(cash.bankName && { bankName: cash.bankName }),
                });
            }
        });
    }
    if (!isArrayValidAndNotEmpty(collections)) {
        return { error: 'Cash collections are required' };
    }
    payload.collections = collections;
    return payload;
};

export const mapPayloadToUiObject = (payload, denominations, bankTransactions) => {
    const uiObject = {
        uuid: payload.uuid,
        user: payload.employee || null,
        date: parseDate(payload.date, applicationDateFormat),
        remarks: payload.notes,
        subCompany: { key: payload.subCompany, value: payload.subCompany },
        printed: payload.printed || false,
    };
    let cashCollections = [];
    let otherCollections = [];
    // collections from different bank configured
    const otherBankCollections = [];
    if (isArrayValidAndNotEmpty(payload.collections)) {
        payload.collections.forEach((coll) => {
            if (coll.paymentMethod === PAYMENT_METHODS_MAP.CASH.value) {
                cashCollections.push({
                    uiUuid: uuidv4(),
                    denomination: coll.denomination,
                    quantity: coll.quantity,
                    amount: coll.amount,
                });
            } else if (
                coll.paymentMethod === PAYMENT_METHODS_MAP.OTHER.value && isValidTextAndNotEmpty(coll.bankName)
            ) {
                otherBankCollections.push({
                    uiUuid: uuidv4(),
                    label: coll.name,
                    paymentMethod: coll.paymentMethod,
                    amount: coll.amount,
                    bankName: coll.bankName || null,
                });
            } else {
                otherCollections.push({
                    uiUuid: uuidv4(),
                    label: coll.name,
                    paymentMethod: coll.paymentMethod,
                    amount: coll.amount,
                });
            }
        });
    }
    const groupedByDenomination = groupArrayOfObjectsByFieldValue(cashCollections, 'denomination');
    cashCollections = [];
    denominations.forEach((d) => {
        const byD = groupedByDenomination[d];
        if (isArrayValidAndNotEmpty(byD)) {
            cashCollections = cashCollections.concat(byD);
        } else {
            cashCollections = cashCollections.concat({
                uiUuid: uuidv4(),
                denomination: d,
                quantity: '',
                amount: '',
            });
        }
    });
    const groupedByMode = groupArrayOfObjectsByFieldValue(otherCollections, 'paymentMethod');
    const groupedByBankName = groupArrayOfObjectsByFieldValue(otherBankCollections, 'bankName');

    otherCollections = [];
    bankTransactions.forEach((t) => {
        if (t.paymentMethod === PAYMENT_METHODS_MAP.OTHER.value) {
            const byD = groupedByBankName[t.bankName];
            const byPaymentMet = groupedByMode[PAYMENT_METHODS_MAP.OTHER.value];
            if (isArrayValidAndNotEmpty(byD)) {
                otherCollections = otherCollections.concat(byD);
            } else if (isArrayValidAndNotEmpty(byPaymentMet)) {
                otherCollections = otherCollections.concat(byPaymentMet);
            } else {
                otherCollections = otherCollections.concat({
                    uiUuid: uuidv4(),
                    label: t.label,
                    paymentMethod: t.paymentMethod,
                    bankName: t.bankName,
                    amount: 0,
                });
            }
        } else {
            const byD = groupedByMode[t.paymentMethod];
            if (isArrayValidAndNotEmpty(byD)) {
                otherCollections = otherCollections.concat(byD);
            } else {
                otherCollections = otherCollections.concat({
                    uiUuid: uuidv4(),
                    label: t.label,
                    paymentMethod: t.paymentMethod,
                    amount: 0,
                });
            }
        }
    });

    uiObject.cashCollections = cashCollections;
    uiObject.otherCollections = otherCollections;
    return uiObject;
};
